/**
 * Pure helpers for the "Today's naps and bedtime" rows (SleepSchedule).
 *
 * The schedule itself is computed SERVER-side from the rows the server holds.
 * A sleep the parent has just started lives in Dexie first and reaches the
 * server on the next drain, so for a moment (longer on a slow connection, or
 * offline for as long as it lasts) the server's answer predates the sleep:
 * it still *predicts* a bedtime for a night sleep that is already running,
 * and the row read "Bedtime ~21:30 · missed" under a hero saying
 * "Asleep · Night since 20:37" (v1.4.3, bug 2). The sync engine now refreshes
 * the schedule once the push lands; this module is the belt-and-braces
 * display rule for the gap in between — the client's own open sleep
 * (useOpenSleep, the same source the hero reads) always wins over a stale
 * server row.
 *
 * Row shape (from SleepSchedule.buildRows):
 *   { key, label, start (ISO), end (ISO|null), taken, inProgress, isBedtime }
 */
import { isOpenSleep, sleepEnd } from './sleepShape';

/** Two ISO instants that describe the same sleep (client vs server copy). */
const SAME_START_TOLERANCE_MS = 60 * 1000;

const toMs = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return Date.parse(value);
};

function sameStart(a, b) {
  const x = toMs(a);
  const y = toMs(b);
  if (Number.isNaN(x) || Number.isNaN(y)) return false;
  return Math.abs(x - y) <= SAME_START_TOLERANCE_MS;
}

function inProgressRow(row, startISO) {
  return { ...row, start: startISO, end: null, taken: true, inProgress: true };
}

/** The client's copy of the sleep a server row describes (same start ± 60 s), or null. */
function localCopyOf(events, row) {
  return (
    (events || []).find(
      (e) => (e?.entityType || e?.type) === 'SLEEP' && sameStart(e?.startTime, row.start)
    ) || null
  );
}

/**
 * Fold the client's open sleep into the schedule rows.
 *
 *   - `events` (the client's SLEEP rows, from useOpenSleep): a row the server
 *     still shows in progress whose LOCAL copy has ended (a wake that has not
 *     been pushed yet) or is too old to count as open is closed — shown as
 *     taken with its local end — never as "asleep since" under a hero that
 *     says Awake. A server row with NO local copy is left alone: it may be a
 *     co-parent's sleep this device has not pulled yet, and hiding a running
 *     sleep is worse than showing an unsynced discard a moment longer.
 *   - the schedule's day (`anchorISO` = `schedule.anchor.time`, `dayStartISO` =
 *     04:00 local on the schedule's date — see openSleepBelongsToDay): an open
 *     sleep from BEFORE the day is not folded in at all. At a 03:00 feed the
 *     night sleep running since 20:37 must not swallow tonight's predicted
 *     bedtime, and a night sleep resumed at 04:10 (the app suggests Night
 *     before 05:00) under a 07:00 anchor is still last night, not tonight.
 *   - no open sleep (or one without a usable start) → rows unchanged
 *   - the server already shows THIS sleep in progress → rows unchanged
 *   - only ONE sleep can be open per baby, so any other row the server still
 *     marks in progress IS this sleep with an unpushed change: the same kind
 *     (bedtime ↔ night, nap ↔ nap) keeps its row and takes the client's
 *     start; a different kind (the category was switched mid-sleep) is
 *     removed before the client's sleep is folded in. Never two rows
 *     "asleep since".
 *   - open NIGHT sleep → the Bedtime row becomes "asleep since HH:MM"
 *     (appended when the server sent no bedtime at all)
 *   - open NAP → the first predicted (not taken) nap becomes the nap in
 *     progress; when every predicted nap is already taken (or none was
 *     predicted) a further "Nap N" row is inserted by start time
 *
 * Returns the same array when nothing changes, a new one otherwise.
 */
export function mergeOpenSleepIntoRows(rows, openSleep, now = Date.now(), { events, dayStartISO, anchorISO } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const nowMs = Number.isNaN(toMs(now)) ? Date.now() : toMs(now);
  let next = list;
  let changed = false;

  // Close server in-progress rows the client knows have ended.
  if (Array.isArray(events)) {
    const closed = next.map((r) => {
      if (!r.inProgress) return r;
      const local = localCopyOf(events, r);
      if (!local || isOpenSleep(local, nowMs)) return r;
      const finished = !!local.endTime || Number(local.data?.durationMinutes) > 0;
      const end = finished ? sleepEnd(local, nowMs) : null;
      return { ...r, inProgress: false, taken: true, end: end ? end.toISOString() : null };
    });
    if (closed.some((r, i) => r !== next[i])) {
      next = closed;
      changed = true;
    }
  }

  const result = () => (changed ? next : list);

  if (!openSleep) return result();
  const startMs = toMs(openSleep.startTime);
  if (Number.isNaN(startMs)) return result();
  const isNight = openSleep?.data?.category === 'night';
  if (!belongsToDay(startMs, isNight, { anchorMs: toMs(anchorISO), dayStartMs: toMs(dayStartISO) })) {
    return result();
  }

  const startISO = new Date(startMs).toISOString();

  // Already reflected by the server — nothing to do.
  const shown = next.find((r) => r.inProgress && sameStart(r.start, startISO) && !!r.isBedtime === isNight);
  if (shown) return result();

  // Any other in-progress row is this sleep with an unpushed start/category
  // change: same kind → re-point it; different kind → drop it.
  let folded = false;
  next = next.reduce((acc, r) => {
    if (!r.inProgress) {
      acc.push(r);
      return acc;
    }
    if (!!r.isBedtime === isNight && !folded) {
      acc.push(inProgressRow(r, startISO));
      folded = true;
    }
    return acc;
  }, []);
  if (folded) return next;

  if (isNight) {
    const idx = next.findIndex((r) => r.isBedtime);
    if (idx >= 0) {
      next[idx] = inProgressRow(next[idx], startISO);
    } else {
      next.push(inProgressRow({ key: 'bedtime', label: 'Bedtime', isBedtime: true }, startISO));
    }
    return next;
  }

  // A nap: prefer the row the server predicted for it — the first nap not yet
  // taken — so the day keeps its shape (Nap 2 stays Nap 2).
  const predicted = next.findIndex((r) => !r.isBedtime && !r.taken && !r.inProgress);
  if (predicted >= 0) {
    next[predicted] = inProgressRow(next[predicted], startISO);
    return next;
  }
  // Every predicted nap is taken (or none was predicted): one more nap,
  // inserted by start time so the timeline stays chronological — before a
  // predicted bedtime, but AFTER a bedtime that already ended (an evening nap
  // following an 18:40–19:20 night sleep lists after it, not above it).
  const napCount = next.filter((r) => !r.isBedtime).length;
  const extra = inProgressRow(
    { key: `nap-${napCount + 1}`, label: `Nap ${napCount + 1}`, isBedtime: false },
    startISO
  );
  const after = next.findIndex((r) => {
    const rowStart = toMs(r.start);
    return !Number.isNaN(rowStart) && rowStart > startMs;
  });
  if (after >= 0) next.splice(after, 0, extra);
  else next.push(extra);
  return next;
}

/**
 * Does the open sleep belong to the schedule's day?
 *
 * The server's rule for "bedtime already taken" is a night-category sleep
 * starting at/after the day's ANCHOR (the morning wake). A night sleep that
 * started before the anchor is last night's — running since 20:37 when the
 * parent opens the app at a 03:00 feed, or resumed at 04:10 under a default
 * 07:00 anchor — and must neither become tonight's Bedtime row nor silence
 * today's sweet-spot reminders. A night sleep that starts within 3 h of the
 * anchor and before local noon is a morning RESETTLE (the night wake at
 * 04:10, back down at 04:40): the server extends the anchor through such a
 * chain once it sees the row, so until then the client treats it as last
 * night too. Naps are cut at 04:00 local on the anchor's date instead: the
 * server's early-riser rule pulls the anchor back to just before a
 * pre-anchor nap, so the anchor is no cutoff for them.
 *
 * Shared by mergeOpenSleepIntoRows (through anchorISO/dayStartISO) and
 * notifications.sweetSpotTargets so the card and the reminders agree.
 * Without a usable cutoff (no anchor) the sleep is taken as today's.
 */
export function openSleepBelongsToDay(openSleep, schedule) {
  const startMs = toMs(openSleep?.startTime);
  if (Number.isNaN(startMs)) return false;
  return belongsToDay(startMs, openSleep?.data?.category === 'night', {
    anchorMs: toMs(schedule?.anchor?.time),
    dayStartMs: toMs(scheduleDayStart(schedule)),
  });
}

/** A night sleep resumed within this long after the morning wake is a resettle, not a bedtime. */
const RESETTLE_GAP_MS = 3 * 60 * 60 * 1000;

function belongsToDay(startMs, isNight, { anchorMs, dayStartMs }) {
  if (isNight && !Number.isNaN(anchorMs)) {
    if (startMs < anchorMs) return false;
    const a = new Date(anchorMs);
    const noonMs = new Date(a.getFullYear(), a.getMonth(), a.getDate(), 12, 0, 0, 0).getTime();
    if (startMs < noonMs && startMs - anchorMs <= RESETTLE_GAP_MS) return false;
    return true;
  }
  if (!Number.isNaN(dayStartMs)) return startMs >= dayStartMs;
  return true;
}

/**
 * 04:00 local on the schedule's own date — the instant before which an open
 * sleep belongs to yesterday's plan. Derived from `schedule.anchor.time`
 * (a nightWake or default anchor is always on the schedule date, in the
 * zone the schedule was requested in — this device's). Null without an
 * anchor, which disables the guard.
 */
export function scheduleDayStart(schedule) {
  const anchor = toMs(schedule?.anchor?.time);
  if (Number.isNaN(anchor)) return null;
  const d = new Date(anchor);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 4, 0, 0, 0).toISOString();
}

/**
 * Next upcoming event from the (merged) rows — the first not-taken,
 * not-in-progress row whose start is still ahead of `now`. Mirrors
 * useSleepSchedule.getNextScheduleEvent but reads the rows the card renders,
 * so a night sleep the server has not seen yet never yields "Next: Bedtime".
 * Returns { label, time, isBedtime } or null.
 */
export function nextEventFromRows(rows, now = Date.now()) {
  const at = toMs(now);
  const list = Array.isArray(rows) ? rows : [];
  return (
    list
      .filter((r) => !r.taken && !r.inProgress && r.start && toMs(r.start) > at)
      .map((r) => ({ label: r.label, time: r.start, isBedtime: !!r.isBedtime }))
      .sort((a, b) => toMs(a.time) - toMs(b.time))[0] || null
  );
}
